import { Dialog, DialogContent, DialogTitle } from '@flatfile/baseline'
import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { AppHTTPService } from '../../../api/http-service'
import { CustomErrorComponent, ErrorBoundaryErrorPage } from '../../../components/Shared/CustomErrorComponent'
import { useActiveEnvironment } from '../../../hooks/useEnvironments'
import { useEventSubscriber } from '../../../hooks/useEventSubscriber'
import { useCollection } from '../../../resources/hooks/useCollection'
import useResource from '../../../resources/hooks/useResource'
import { useCreateResource, useUpdateResource } from '../../../resources/hooks/useResourceMutation'
import getCollection from '../../../resources/support/getCollection'
import { logger } from '../../../utils/logging'
import { SpaceStep } from '../AutoBuild/SpaceStep'
import { ConfigureFlatfileOSForm, FlatfileOSParams } from './ConfigureFlatfileOSForm'
import { FlatfileOSLoadingScreen, LoadingStage } from './FlatfileOSLoadingScreen'
import { Header } from './Header'

// Inline the getJob function that was previously in Shared.tsx
const getJob = async (id: string) => {
  return AppHTTPService.client.jobs.get(id)
}

const ConfigureStep: React.FC<{ onSubmit: (_data: any) => void; error?: string }> = ({ onSubmit, error }) => {
  return (
    <div className='flex flex-col h-full'>
      <Header showCloseButton={true} />
      <div className='flex-1 flex flex-col items-center justify-start w-full overflow-y-auto'>
        <div className='flex-none w-full'>
          <ConfigureFlatfileOSForm onSubmit={onSubmit} error={error} />
        </div>
      </div>
    </div>
  )
}

export const FlatfileOSContent: React.FC<{}> = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blueprintJobId, setBlueprintJobId] = useState<string | null>(null)
  const [appIdInvalid, setAppIdInvalid] = useState(false)
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('initializing')
  const [jobStatus, setJobStatus] = useState<string>('')
  const apps = useCollection('APPS')
  const navigate = useNavigate()
  const { id: appId, step } = useParams<{ id: string; step: string }>()
  const [createAutoBuild] = useCreateResource('AUTOBUILD')
  const { environment } = useActiveEnvironment()
  const [updateApp] = useUpdateResource('APPS')
  const app = useResource('APPS', appId!)

  useEffect(() => {
    if (appId) {
      const existingApp = apps.find((a) => a.id === appId)
      if (existingApp) {
        return
      }
    }
    setAppIdInvalid(true)
  }, [apps, appId])

  const handleConfigureFlatfileOS = useCallback(
    async ({ companyUrl }: FlatfileOSParams) => {
      if (!companyUrl) {
        setError('Company URL is required')
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        setBlueprintJobId(null)
        setLoadingStage('initializing')

        const formData = new FormData()
        formData.append('companyUrl', companyUrl)

        await createAutoBuild({
          appId: appId!,
          environmentId: environment.id,
          formData,
        })
      } catch (e) {
        logger.error('Error in Flatfile OS process:', e)
        setIsLoading(false)
        setError('An error occurred while configuring Flatfile OS. Please try again.')
      }
    },
    [createAutoBuild, environment.id, appId]
  )

  const handleEventForCurrentApp = useCallback(
    (event: any, operations: string[], action: () => void) => {
      // First check if this is for the current app
      if (event?.context?.appId !== appId) {
        return
      }

      // Then check if this is one of our expected operations
      if (!operations.includes(event?.payload?.operation)) {
        return
      }

      // Only if both checks pass, execute the action
      action()
    },
    [appId]
  )

  const handleSuccess = useCallback(
    async (targetAppId: string) => {
      if (!app?.metadata?.flatfileos || !app?.metadata?.templateCreated) {
        await updateApp(targetAppId, { metadata: { ...app?.metadata, flatfileos: true, templateCreated: true } })
        await getCollection('APPS').refresh()
      }
      setBlueprintJobId(null)
      setLoadingStage('loading-space')

      // Allow a brief moment to show "loading space" message before transitioning
      setTimeout(() => {
        setIsLoading(false)
        navigate(`/apps/custom/${appId}/build/template`)
      }, 1000)
    },
    [app?.metadata, navigate, updateApp, appId]
  )

  const handleError = useCallback(
    async ({ event, errorMessage }: { event?: any; errorMessage?: string }) => {
      if (event) {
        const job = await getJob(event.context.jobId)
        const outcomeMessage = job.data?.outcome?.message
        const reasonMatch = outcomeMessage.match(/Reason: (.*)/)
        const reason = reasonMatch?.[1] || 'Unable to generate your app. Please try again.'
        setBlueprintJobId(null)
        setError(reason)
        setIsLoading(false)
      }
      if (errorMessage) {
        setBlueprintJobId(null)
        setError(errorMessage)
        setIsLoading(false)
      }
    },
    [setError, setIsLoading]
  )

  const isRelevantApp = useCallback(
    (eventAppId?: string) => {
      return eventAppId === appId
    },
    [appId]
  )

  const handleJobEvent = useCallback(
    (event: any, operations: string[], requireComplete: boolean = false) => {
      handleEventForCurrentApp(event, operations, async () => {
        // For job:updated events, we need to check the status
        if (requireComplete && event.payload.status !== 'complete') {
          return
        }

        await handleSuccess(event.context.appId)
      })
    },
    [handleEventForCurrentApp, handleSuccess]
  )

  // Poll for job status updates
  useEffect(() => {
    if (!blueprintJobId) return

    setLoadingStage('generating')

    const pollJobStatus = async () => {
      try {
        const { data: job } = await getJob(blueprintJobId)
        if (job.info) {
          setJobStatus(job.info)
        }
      } catch (err) {
        logger.error('Error polling job status:', err)
      }
    }

    // Poll immediately and then every 5 seconds
    pollJobStatus()
    const intervalId = setInterval(pollJobStatus, 5000)

    return () => {
      clearInterval(intervalId)
    }
  }, [blueprintJobId])

  //Checking multiple operations and event types to provide redundancy for dropped events
  useEventSubscriber(['job:completed'], (event) =>
    handleJobEvent(event, ['ai-generate-blueprint', 'ai-generate-blueprint-constraints'], false)
  )

  useEventSubscriber(['job:updated'], (event) =>
    handleJobEvent(event, ['ai-generate-blueprint', 'ai-generate-blueprint-constraints'], true)
  )

  useEventSubscriber(['job:created', 'job:ready'], (event) => {
    if (
      isRelevantApp(event?.context?.appId) &&
      event?.payload?.operation === 'ai-generate-blueprint' &&
      !!event?.context?.jobId &&
      blueprintJobId !== event?.context?.jobId
    ) {
      setBlueprintJobId(event?.context?.jobId)
    }
  })

  useEventSubscriber(['job:failed'], async (event) => {
    handleEventForCurrentApp(event, ['ai-generate-blueprint'], async () => {
      if (event.context.jobId === blueprintJobId || blueprintJobId === null) {
        handleError({ event })
      }
    })
  })

  if (appIdInvalid) {
    return (
      <CustomErrorComponent
        page='flatfileos-flow-invalidappId'
        message='Unable to find App. Please check the URL or try again.'
      />
    )
  }

  if (isLoading) {
    return <FlatfileOSLoadingScreen stage={loadingStage} jobStatus={jobStatus} />
  }

  if (error) {
    return <ConfigureStep onSubmit={handleConfigureFlatfileOS} error={error} />
  }

  switch (step) {
    case 'configure':
      return <ConfigureStep onSubmit={handleConfigureFlatfileOS} />
    case 'template':
    case 'settings':
      return appId ? <SpaceStep appId={appId} /> : <Navigate to='/flatfile-os/configure' />
    default:
      return <Navigate to='/flatfile-os/configure' replace />
  }
}

export const FlatfileOSFlow: React.FC = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'CLOSE_FLATFILE_OS') {
        await getCollection('APPS').refresh()
        // Use the appId from the message to navigate to the app's list view
        if (event.data?.appId) {
          navigate(`/apps/custom/${event.data.appId}/list/recent/`)
        } else {
          // Fallback to home if no appId is provided
          handleClose()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleClose = () => {
    navigate('/home')
  }

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className='p-0 max-w-full h-full border-none z-[200] flex flex-col gap-0' fullscreen>
        <DialogTitle className='sr-only'>Flatfile OS Configuration</DialogTitle>
        <div className='bg-surface-base-elevated flex-1 flex flex-col overflow-hidden'>
          <ErrorBoundary
            fallbackRender={({ error }) => (
              <ErrorBoundaryErrorPage
                error={error}
                page='FlatfileOSFlow.tsx'
                message='There was an error loading Flatfile OS.'
              />
            )}
          >
            <Suspense fallback={<FlatfileOSLoadingScreen stage='initializing' />}>
              <FlatfileOSContent />
            </Suspense>
          </ErrorBoundary>
        </div>
      </DialogContent>
    </Dialog>
  )
}
